import styled from 'styled-components';

import { mainGridGapColor, cFFFFFF, c00EE91, cFF005C } from '#/colors';

import Tooltip, { HelpUnderline } from '#/components/shared/Tooltip';
import ValueChange from '#/components/shared/ValueChange';
import { VerticalSpacer } from '#/components/shared/Spacer';
import { MEDIA } from '#/components/shared/media-queries';
import MarketsMenu from '#/components/shared/markets-menu/MarketsMenu';

import {
  MarketSummaryView,
  useMarketsSummaryView,
} from '#/features/perpetuals/market-info/markets-summary-context';
import {
  LastTradeProvider,
  useLastTradeView,
} from '#/features/perpetuals/trades/last-trade-view-context';

import Indicator from './Indicator';

interface Props {
  readonly className?: string;
}

export default function MarketInfo({ className }: Props) {
  const { activeMarketSummary } = useMarketsSummaryView();

  return (
    <Container className={className}>
      <MarketsMenu />
      <MarketSummary>
        <LastTradeProvider>
          <Indicator
            label="Last Price"
            value={<LastTradedPrice />}
            css="min-width: 90px;"
          />
        </LastTradeProvider>
        <VerticalSpacer />
        <Indicator
          label="Mark Price"
          value={
            <span data-testid="market-info-mark-price">
              {activeMarketSummary.oraclePrice}
            </span>
          }
          css="min-width: 80px;"
        />
        <VerticalSpacer />
        <Indicator
          label="Spot Oracle Price"
          value={activeMarketSummary.underlyingPrice}
          css="min-width: 120px;"
        />
        <VerticalSpacer />
        <Indicator
          label="Open Interest"
          value={activeMarketSummary.openInterest}
          css="min-width: 100px;"
        />
        <VerticalSpacer />
        <Indicator
          label="24h Volume"
          value={activeMarketSummary.volume24h}
          css="min-width: 80px;"
        />
        <VerticalSpacer />
        <FundingRate marketSummary={activeMarketSummary} />
      </MarketSummary>
    </Container>
  );
}

interface FundingRateProps {
  readonly marketSummary: MarketSummaryView;
}

function FundingRate(props: FundingRateProps) {
  return (
    <Indicator
      label={
        <Tooltip label="Eight hour funding rate that is continuously accrued on positions and settled upon any trade">
          <HelpUnderline>8h Funding Rate</HelpUnderline>
        </Tooltip>
      }
      value={
        <ValueChange direction={props.marketSummary.fundingRateChangeDirection}>
          {props.marketSummary.fundingRate8h}
        </ValueChange>
      }
    />
  );
}

function LastTradedPrice() {
  const lastTrade = useLastTradeView();
  return (
    <ColoredPrice as={LastTradedValue} side={lastTrade.side}>
      {lastTrade.price}
    </ColoredPrice>
  );
}

const Container = styled.div`
  display: grid;
  border-bottom: 1px solid ${mainGridGapColor};

  /* 0px – md */
  grid-template-columns: 216px 1fr;

  @media ${MEDIA.md} {
    grid-template-columns: 240px 1fr;
  }
`;

const MarketSummary = styled.div`
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 24px;
  overflow: auto;

  & > * {
    flex-shrink: 0;
  }
`;

const LastTradedValue = styled.span`
  font-size: 18px;
  color: ${cFFFFFF};
  font-weight: 700;
  min-width: 90px;
`;

const ColoredPrice = styled.span<{ readonly side: 'BUY' | 'SELL' }>`
  color: ${({ side }) => (side === 'BUY' ? c00EE91 : cFF005C)};
`;
